"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/shared/container";
import { Reveal, SectionHead, Sky } from "@/components/sections/sky/sky-ui";
import { DemoButton } from "@/components/demo-button";
import { RefreshCw, Plug, CalendarCheck, Check } from "lucide-react";
import { track } from "@/lib/posthog-tracking";

const SLIDERS = {
  visitTime: { label: "Średni czas wizyty (minuty)", min: 5, max: 60, step: 5, default: 15 },
  visitsPerDay: { label: "Liczba wizyt dziennie", min: 20, max: 300, step: 10, default: 80 },
  hourlyRate: { label: "Stawka za godzinę pracy (PLN)", min: 50, max: 300, step: 10, default: 100 },
};

interface SavingsCalculatorProps {
  headline: string;
  description: string;
  ctaLabel: string;
  ctaHref?: string;
  usePopup?: boolean;
  popupSource?: string;
  sectionId?: string;
  sectionClassName?: string;
  eyebrow?: string;
}

export function SavingsCalculator({
  headline,
  description,
  ctaLabel,
  ctaHref,
  usePopup = false,
  popupSource = "savings-calculator",
  sectionId,
  sectionClassName = "py-16 md:py-24",
  eyebrow = "Kalkulator oszczędności",
}: SavingsCalculatorProps) {
  const [visitTime, setVisitTime] = useState(SLIDERS.visitTime.default);
  const [visitsPerDay, setVisitsPerDay] = useState(SLIDERS.visitsPerDay.default);
  const [hourlyRate, setHourlyRate] = useState(SLIDERS.hourlyRate.default);
  const interactedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const savedMinutesPerVisit = 2;
  const savedMinutesPerDay = visitsPerDay * savedMinutesPerVisit;
  const savedHoursPerDay = savedMinutesPerDay / 60;
  const dailySavings = savedHoursPerDay * hourlyRate;
  const monthlySavings = dailySavings * 20;
  const yearlySavings = monthlySavings * 12;
  const additionalVisits = Math.floor(savedMinutesPerDay / visitTime);

  const handleSliderChange = (slider: string, value: number, setter: (v: number) => void) => {
    setter(value);
    if (!interactedRef.current) {
      interactedRef.current = true;
      track("calculator_interacted", { source: popupSource });
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      track("calculator_slider_changed", {
        slider,
        value,
        visit_time: slider === "visit_time" ? value : visitTime,
        visits_per_day: slider === "visits_per_day" ? value : visitsPerDay,
        hourly_rate: slider === "hourly_rate" ? value : hourlyRate,
        monthly_savings: Math.round(monthlySavings),
        source: popupSource,
      });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const ctaClass =
    "h-12 px-8 text-base font-semibold shadow-xl shadow-[#021A9E]/25 whitespace-normal text-center leading-snug";

  return (
    <section id={sectionId} className={sectionClassName}>
      <Container>
        <SectionHead eyebrow={eyebrow} title={headline} lead={description} />

        <Reveal className="mx-auto mt-12 max-w-5xl">
          <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_24px_60px_-36px_rgba(2,26,158,0.35)] ring-1 ring-black/[0.05]">
            <div className="grid gap-6 p-4 md:grid-cols-2 md:gap-8 md:p-6">
              <div className="order-2 flex flex-col justify-center gap-8 p-2 md:order-1 md:p-4">
                <SliderControl
                  label={SLIDERS.visitTime.label}
                  value={visitTime}
                  onChange={(v) => handleSliderChange("visit_time", v, setVisitTime)}
                  min={SLIDERS.visitTime.min}
                  max={SLIDERS.visitTime.max}
                  step={SLIDERS.visitTime.step}
                  unit="min"
                />
                <SliderControl
                  label={SLIDERS.visitsPerDay.label}
                  value={visitsPerDay}
                  onChange={(v) => handleSliderChange("visits_per_day", v, setVisitsPerDay)}
                  min={SLIDERS.visitsPerDay.min}
                  max={SLIDERS.visitsPerDay.max}
                  step={SLIDERS.visitsPerDay.step}
                />
                <SliderControl
                  label={SLIDERS.hourlyRate.label}
                  value={hourlyRate}
                  onChange={(v) => handleSliderChange("hourly_rate", v, setHourlyRate)}
                  min={SLIDERS.hourlyRate.min}
                  max={SLIDERS.hourlyRate.max}
                  step={SLIDERS.hourlyRate.step}
                  unit="zł"
                />
              </div>

              {/* Wynik na głębokim niebie — białe liczby, pełny kontrast */}
              <div className="relative order-1 flex flex-col items-center justify-center overflow-hidden rounded-[22px] p-8 text-center text-white md:order-2">
                <Sky variant="deep" />
                <span className="relative text-sm font-medium uppercase tracking-wider text-white/85">
                  Oszczędzasz miesięcznie
                </span>
                <span className="relative mt-2 text-5xl font-bold tabular-nums md:text-6xl">
                  {Math.round(monthlySavings).toLocaleString("pl-PL")} zł
                </span>
                <span className="relative mt-4 text-sm text-white/85">
                  Co daje rocznie:{" "}
                  <strong className="text-white">{Math.round(yearlySavings).toLocaleString("pl-PL")} zł</strong>
                </span>
                <div className="relative mt-6 flex flex-col gap-1 text-sm text-white/85">
                  <span>
                    Zaoszczędzony czas dziennie: <strong className="text-white">{savedHoursPerDay.toFixed(1)}h</strong>
                  </span>
                  <span>
                    Dodatkowe wizyty możliwe: <strong className="text-white">{additionalVisits}</strong>
                  </span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="border-t border-black/[0.05] bg-[#F5F7FB] p-6 md:p-10">
              <div className="mx-auto max-w-xl text-center">
                <h3 className="text-xl font-bold text-foreground md:text-2xl">
                  Sprawdź, ile Vetina będzie kosztować w Twojej placówce
                </h3>
                <p className="mt-2 text-muted-foreground">30-minutowa prezentacja • Bez zobowiązań</p>

                <div className="mt-6">
                  {usePopup ? (
                    <DemoButton source={popupSource} size="lg" className={ctaClass}>
                      {ctaLabel}
                    </DemoButton>
                  ) : (
                    <Button asChild size="lg" className={ctaClass}>
                      <Link href={ctaHref!}>{ctaLabel}</Link>
                    </Button>
                  )}
                </div>

                <div className="mx-auto mt-6 flex w-fit flex-col items-start gap-2.5 text-left text-sm text-foreground/80">
                  {[
                    { icon: RefreshCw, text: "Stała cena z automatycznymi aktualizacjami" },
                    { icon: Plug, text: "Integracje z laboratoriami i aparaturą przez Vetina Bridge" },
                    { icon: CalendarCheck, text: "Vetina Booking — klienci sami rezerwują wizyty online" },
                    { icon: Check, text: "Bezpłatna migracja danych z każdego systemu" },
                  ].map((f) => (
                    <span key={f.text} className="inline-flex items-center gap-2.5">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-white">
                        <f.icon className="h-3.5 w-3.5" />
                      </span>
                      {f.text}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function SliderControl({
  label,
  value,
  onChange,
  min,
  max,
  step,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  unit?: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{label}</label>
        <span className="text-sm font-bold text-primary tabular-nums">
          {value}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
        className="w-full"
      />
    </div>
  );
}
